import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { CityStatePanel } from '../city/CityStatePanel';
import {
  loadCities,
  persistCitySelection,
  resolveCityContext,
  restoredCitySelection,
  type City,
  type CityContextStatus,
  type CityPolicy,
  type DetectedLocation,
} from '../city/cityContext';
import type { SessionResponse } from '../ProfileScreen';
import { ScannerLauncher } from '../ScannerLauncher';
import { BottomSheet } from '../ui/BottomSheet';
import {
  CONSUMER_PATH_BY_SECTION,
  DesktopHeader,
  MobileBottomNav,
  MobileHeader,
  type ConsumerNavSection,
} from '../ui/ConsumerNavigation';
import { SearchUiBridge } from '../ui/SearchUiBridge';
import {
  UserContextSheet,
  useUserContextActions,
  useUserContextData,
} from '../userContext/UserContext';

type EventsRouteShellProps = {
  children: ReactNode;
  externalSession?: SessionResponse | null;
  onSessionChange?: (session: SessionResponse) => void;
};

type FrontendConfigResponse = {
  frontend: {
    city_policy: CityPolicy;
  };
};

type CitySearchState = 'idle' | 'loading' | 'ready' | 'error';

function activeSectionForPath(path = window.location.pathname): ConsumerNavSection | null {
  if (/^\/offers(?:\/|$)/.test(path)) return 'offers';
  if (/^\/profile(?:\/|$)/.test(path)) return 'profile';
  if (/^\/venue(?:\/|$)/.test(path)) return 'map';
  if (/^\/(?:pulse|events|posts)(?:\/|$)/.test(path)) return 'feed';
  return null;
}

function navigate(section: ConsumerNavSection): void {
  window.location.assign(CONSUMER_PATH_BY_SECTION[section]);
}

export function EventsRouteShell({ children, externalSession, onSessionChange }: EventsRouteShellProps) {
  const [localSession, setLocalSession] = useState<SessionResponse | null>(null);
  const [userContextOpen, setUserContextOpen] = useState(false);
  const [citySelectorOpen, setCitySelectorOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [cityChoices, setCityChoices] = useState<City[]>([]);
  const [citySearchState, setCitySearchState] = useState<CitySearchState>('idle');
  const [citySearchRevision, setCitySearchRevision] = useState(0);
  const [citySelectionError, setCitySelectionError] = useState<string | null>(null);
  const [choosingCityId, setChoosingCityId] = useState<string | null>(null);
  const [activeCity, setActiveCity] = useState<City | null>(null);
  const [cityPolicy, setCityPolicy] = useState<CityPolicy | null>(null);
  const [cityStatus, setCityStatus] = useState<CityContextStatus>('location_unresolved');
  const [detectedLocation, setDetectedLocation] = useState<DetectedLocation | null>(null);
  const [cityResolving, setCityResolving] = useState(false);
  const cityRequestRevision = useRef(0);
  const ownsSession = externalSession === undefined;
  const session = ownsSession ? localSession : externalSession;
  const activeSection = activeSectionForPath();

  const updateSession = useCallback((nextSession: SessionResponse) => {
    if (ownsSession) setLocalSession(nextSession);
    onSessionChange?.(nextSession);
  }, [onSessionChange, ownsSession]);

  useEffect(() => {
    if (!ownsSession) return undefined;

    const controller = new AbortController();

    fetch('/api/v1/auth/session', {
      signal: controller.signal,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => response.ok ? response.json() as Promise<SessionResponse> : null)
      .then((value) => setLocalSession(value))
      .catch(() => setLocalSession(null));

    return () => controller.abort();
  }, [ownsSession]);

  useEffect(() => {
    const controller = new AbortController();

    fetch('/api/v1/frontend-config', {
      signal: controller.signal,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`frontend_config_${response.status}`);
        return response.json() as Promise<FrontendConfigResponse>;
      })
      .then((config) => setCityPolicy(config.frontend.city_policy))
      .catch(() => setCityPolicy(null));

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const restored = restoredCitySelection();
    if (!restored) return undefined;

    const revision = ++cityRequestRevision.current;
    const controller = new AbortController();
    setCityResolving(true);

    void resolveCityContext({ city: restored.city }, controller.signal)
      .then((result) => {
        if (revision !== cityRequestRevision.current) return;
        setCityPolicy(result.policy);
        setCityStatus(result.status);
        setDetectedLocation(result.detected_location);
        setCityResolving(false);

        if (result.status === 'available' && result.city) {
          setActiveCity(result.city);
          persistCitySelection(result.city, restored.manual);
          window.dispatchEvent(new CustomEvent('hopper:city-changed', { detail: { city: result.city.id } }));
        } else {
          setActiveCity(null);
        }
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        if (revision === cityRequestRevision.current) {
          setCityResolving(false);
          setCityStatus('location_unresolved');
        }
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!citySelectorOpen || cityPolicy?.mode === 'pilot_only') return undefined;

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setCitySearchState('loading');
      setCitySelectionError(null);
      void loadCities(citySearch, controller.signal)
        .then((cities) => {
          setCityChoices(cities);
          setCitySearchState('ready');
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setCityChoices([]);
          setCitySearchState('error');
        });
    }, 180);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [citySearch, citySearchRevision, citySelectorOpen, cityPolicy?.mode, cityPolicy?.revision]);

  const applyAvailableCity = useCallback((city: City, manual: boolean) => {
    persistCitySelection(city, manual);
    setActiveCity(city);
    setCityStatus('available');
    setDetectedLocation(null);
    setCitySelectorOpen(false);
    setCitySelectionError(null);
    window.dispatchEvent(new CustomEvent('hopper:city-changed', { detail: { city: city.id } }));
  }, []);

  const chooseCity = useCallback((city: City) => {
    if (choosingCityId) return;

    const revision = ++cityRequestRevision.current;
    setChoosingCityId(city.id);
    setCitySelectionError(null);

    void resolveCityContext({ city: city.id })
      .then((result) => {
        if (revision !== cityRequestRevision.current) return;
        setCityPolicy(result.policy);
        setCityStatus(result.status);
        setDetectedLocation(result.detected_location);

        if (result.status === 'available' && result.city) {
          applyAvailableCity(result.city, true);
        } else {
          setActiveCity(null);
          setCitySelectionError('Этот город сейчас недоступен. Выберите другой город.');
        }
      })
      .catch(() => {
        if (revision === cityRequestRevision.current) {
          setCitySelectionError('Не удалось проверить город. Попробуйте ещё раз.');
        }
      })
      .finally(() => {
        if (revision === cityRequestRevision.current) setChoosingCityId(null);
      });
  }, [applyAvailableCity, choosingCityId]);

  const locateCity = useCallback(() => {
    if (!navigator.geolocation || cityResolving) return;

    const revision = ++cityRequestRevision.current;
    setCityResolving(true);
    setCitySelectionError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        void resolveCityContext({ latitude: position.coords.latitude, longitude: position.coords.longitude })
          .then((result) => {
            if (revision !== cityRequestRevision.current) return;
            setCityPolicy(result.policy);
            setCityStatus(result.status);
            setDetectedLocation(result.detected_location);

            if (result.status === 'available' && result.city) {
              applyAvailableCity(result.city, false);
            } else {
              setActiveCity(null);
            }
          })
          .catch(() => {
            if (revision === cityRequestRevision.current) {
              setCityStatus('location_unresolved');
              setCitySelectionError('Не удалось определить город. Выберите его вручную.');
            }
          })
          .finally(() => {
            if (revision === cityRequestRevision.current) setCityResolving(false);
          });
      },
      () => {
        if (revision !== cityRequestRevision.current) return;
        setCityResolving(false);
        setCityStatus('location_unresolved');
        setCitySelectionError('Геолокация недоступна. Выберите город вручную.');
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 },
    );
  }, [applyAvailableCity, cityResolving]);

  const handleScanOpen = useCallback(() => {
    window.dispatchEvent(new Event('hopper:open-scanner'));
  }, []);

  const cityName = activeCity?.name ?? cityPolicy?.pilot_city?.name ?? 'Выберите город';
  const userContext = useUserContextData(session ?? null, activeCity?.id ?? '', cityName);
  const userContextActions = useUserContextActions({
    onSessionChange: updateSession,
    onClose: () => setUserContextOpen(false),
  });

  const cityPanel = (
    <CityStatePanel
      status={cityStatus}
      policy={cityPolicy}
      detected={detectedLocation}
      resolving={cityResolving}
      onPilotCity={() => {
        if (cityPolicy?.pilot_city) chooseCity(cityPolicy.pilot_city);
      }}
      onScan={handleScanOpen}
      onChoose={() => setCitySelectorOpen(true)}
      onLocate={locateCity}
    />
  );

  return (
    <div className="hopper-app-shell events-route-shell">
      <DesktopHeader
        session={session ?? null}
        cityName={cityName}
        activeSection={activeSection}
        onNavigate={navigate}
        onNotifications={() => window.location.assign('/notifications')}
        onCityClick={() => setCitySelectorOpen(true)}
      />
      <MobileHeader
        userContext={userContext}
        onNotifications={() => window.location.assign('/notifications')}
        onOpenUserContext={() => setUserContextOpen(true)}
        cityName={cityName}
        onCityClick={() => setCitySelectorOpen(true)}
      />

      {children}

      <BottomSheet
        open={citySelectorOpen}
        onClose={() => setCitySelectorOpen(false)}
        title="Выберите город"
        rootClassName="city-selector-sheet"
        maxHeight="72dvh"
      >
        <div className="city-selector-sheet__content">
          {cityPolicy?.mode === 'pilot_only' ? (
            <div className="city-selector-results">
              <p><strong>Хоппер пока работает во Владивостоке.</strong></p>
              <p>Другие города пока недоступны для публичного просмотра.</p>
              {cityPolicy.pilot_city && (
                <button
                  type="button"
                  disabled={choosingCityId !== null}
                  onClick={() => chooseCity(cityPolicy.pilot_city!)}
                >
                  <strong>{choosingCityId === cityPolicy.pilot_city.id ? 'Открываем…' : cityPolicy.pilot_city.name}</strong>
                  {cityPolicy.pilot_city.region_name && <span>{cityPolicy.pilot_city.region_name}</span>}
                </button>
              )}
              <button className="city-locate-button" type="button" onClick={handleScanOpen}>Сканировать чек</button>
            </div>
          ) : (
            <>
              <label className="city-selector-search">
                <span className="pulse-visually-hidden">Поиск города</span>
                <input
                  autoFocus
                  aria-label="Поиск города"
                  value={citySearch}
                  onChange={(event) => setCitySearch(event.target.value)}
                  placeholder="Город или регион"
                  type="search"
                />
              </label>
              <button className="city-locate-button" type="button" disabled={cityResolving} onClick={locateCity}>
                {cityResolving ? 'Определяем город…' : 'Определить мой город'}
              </button>

              {citySelectionError && <div className="city-selector-feedback is-error" role="alert">{citySelectionError}</div>}
              {citySearchState === 'loading' && <div className="city-selector-feedback" role="status">Ищем доступные города…</div>}
              {citySearchState === 'error' && (
                <div className="city-selector-feedback is-error" role="alert">
                  <span>Не удалось загрузить города.</span>
                  <button type="button" onClick={() => setCitySearchRevision((value) => value + 1)}>Повторить</button>
                </div>
              )}
              {citySearchState === 'ready' && (
                <div className="city-selector-results">
                  {cityChoices.map((city) => (
                    <button
                      key={city.id}
                      type="button"
                      disabled={choosingCityId !== null}
                      onClick={() => chooseCity(city)}
                    >
                      <strong>{choosingCityId === city.id ? 'Проверяем…' : city.name}</strong>
                      {city.region_name && <span>{city.region_name}</span>}
                    </button>
                  ))}
                  {cityChoices.length === 0 && <p>По запросу доступных городов не найдено.</p>}
                </div>
              )}

              {!activeCity && cityStatus !== 'location_unresolved' && cityStatus !== 'available' && cityPanel}
            </>
          )}
        </div>
      </BottomSheet>

      <SearchUiBridge />
      <ScannerLauncher />
      <UserContextSheet
        open={userContextOpen}
        onClose={() => setUserContextOpen(false)}
        data={userContext}
        actions={userContextActions}
      />
      <MobileBottomNav activeSection={activeSection} onNavigate={navigate} onScan={handleScanOpen} />
    </div>
  );
}
